import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Home, Search, Zap } from 'lucide-react';

/* ─── Animated particle canvas (matches CoreAdvantagePage style) ─── */
type Particle = { x: number; y: number; vx: number; vy: number; r: number; pulse: number };

const ParticleCanvas = () => {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let w: number, h: number, pts: Particle[], raf: number;

    const init = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
      pts = Array.from({ length: 55 }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 1.5 + 0.5,
        pulse: Math.random() * Math.PI * 2,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.pulse += 0.018;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
      });
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
          if (d < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(255,93,34,${(1 - d / 120) * 0.18})`;
            ctx.lineWidth = 0.7;
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }
      pts.forEach(p => {
        const pr = p.r * (1 + 0.3 * Math.sin(p.pulse));
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, pr * 3.5);
        g.addColorStop(0, 'rgba(255,93,34,0.9)');
        g.addColorStop(1, 'rgba(255,93,34,0)');
        ctx.beginPath();
        ctx.arc(p.x, p.y, pr * 3.5, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };

    init(); draw();
    const ro = new ResizeObserver(init);
    ro.observe(canvas);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);

  return (
    <canvas
      ref={ref}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    />
  );
};

/* ─── Reveal animation (intersection-based, matches site pattern) ─── */
const Reveal = ({ children, delay = 0, className = '' }: {
  children: React.ReactNode; delay?: number; className?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } },
      { threshold: 0.08 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: vis ? 1 : 0,
        transform: vis ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)',
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

/* ─── Glitching 404 digits ─── */
const GlitchDigit = ({ char, delay }: { char: string; delay: number }) => {
  const [display, setDisplay] = useState(char);
  const chars = '0123456789ABCDEF';

  useEffect(() => {
    let iterations = 0;
    const max = 10;
    const interval = setInterval(() => {
      if (iterations >= max) {
        setDisplay(char);
        clearInterval(interval);
        return;
      }
      setDisplay(chars[Math.floor(Math.random() * chars.length)]);
      iterations++;
    }, 60);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      setDisplay(char);
    }, delay * 1000 + max * 60 + 200);

    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [char, delay]);

  return <span>{display}</span>;
};

/* ─── Quick links ─── */
const QUICK_LINKS = [
  { label: 'Platform Overview', href: '/platform/about' },
  { label: 'Lead to Order', href: '/platform/lead-to-order' },
  { label: 'Order to Cash', href: '/platform/order-to-cash' },
  { label: 'Cloud Distribution', href: '/industries/cloud-distribution' },
  { label: 'Telco to Techco', href: '/industries/telco-to-techco' },
  { label: 'Case Studies', href: '/case-studies' },
];

/* ─── Page ─── */
export default function NotFoundPage() {
  const location = useLocation();
  const badPath = location.pathname;

  return (
    <div className="relative min-h-screen bg-slate-950 overflow-hidden flex flex-col">

      {/* Dot grid background */}
      <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.028)_1px,transparent_1px)] bg-[length:28px_28px]" />

      {/* Ambient orbs */}
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          top: '-8%', right: '-6%',
          width: 420, height: 420,
          background: '#ff5d22',
          opacity: 0.09,
          filter: 'blur(130px)',
          animation: 'orbFloat 22s ease-in-out infinite',
        }}
      />
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          bottom: '-10%', left: '-5%',
          width: 340, height: 340,
          background: '#3b82f6',
          opacity: 0.07,
          filter: 'blur(110px)',
          animation: 'orbFloat 28s ease-in-out 5s infinite',
        }}
      />

      <style>{`
        @keyframes orbFloat {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -20px) scale(1.05); }
          66% { transform: translate(-15px, 14px) scale(0.96); }
        }
        @keyframes pulseRing {
          0% { transform: scale(0.85); opacity: 0.7; }
          100% { transform: scale(1.5); opacity: 0; }
        }
      `}</style>

      {/* Particle network */}
      <ParticleCanvas />

      {/* Main content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-20">

        {/* Badge */}
        <Reveal delay={0}>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-[9px] font-black uppercase tracking-[0.22em] text-white/60 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ff5d22]" />
            Revenue Orchestration Platform
          </span>
        </Reveal>

        {/* 404 heading with glitch */}
        <Reveal delay={0.08}>
          <div className="relative flex items-center justify-center mb-2">
            {/* Pulse ring behind the number */}
            <div
              className="absolute rounded-full border border-[#ff5d22]/30"
              style={{ width: 220, height: 220, animation: 'pulseRing 2.4s ease-out infinite' }}
            />
            <h1
              className="text-[clamp(96px,18vw,180px)] font-black leading-none tracking-tighter text-transparent select-none"
              style={{
                WebkitTextStroke: '2px rgba(255,93,34,0.6)',
                textShadow: '0 0 80px rgba(255,93,34,0.25)',
              }}
            >
              <GlitchDigit char="4" delay={0.1} />
              <GlitchDigit char="0" delay={0.3} />
              <GlitchDigit char="4" delay={0.5} />
            </h1>
          </div>
        </Reveal>

        {/* Title */}
        <Reveal delay={0.18}>
          <h2 className="text-2xl md:text-3xl font-black tracking-tighter text-white text-center mb-3">
            This route isn't{' '}
            <span className="bg-gradient-to-r from-[#ff5d22] to-red-500 bg-clip-text text-transparent">
              orchestrated.
            </span>
          </h2>
        </Reveal>

        {/* Description */}
        <Reveal delay={0.24}>
          <p className="text-sm text-white/55 font-medium text-center max-w-md leading-relaxed mb-2">
            The path{' '}
            <code className="px-2 py-0.5 rounded bg-white/8 border border-white/12 text-[#ff8a5c] text-xs font-mono">
              {badPath}
            </code>{' '}
            doesn't exist in our lifecycle. It may have moved, been deprecated, or never existed.
          </p>
        </Reveal>

        {/* CTA buttons */}
        <Reveal delay={0.30}>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-7 mb-12">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#ff5d22] text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#d41820] transition-all shadow-lg hover:shadow-xl hover:shadow-[#ff5d22]/25"
            >
              <Home size={13} />
              Back to Home
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white/8 text-white text-[10px] font-black uppercase tracking-widest border border-white/15 hover:bg-white/14 transition-all"
            >
              Contact Us
              <ArrowRight size={13} />
            </Link>
          </div>
        </Reveal>

        {/* Divider */}
        <Reveal delay={0.35}>
          <div className="w-full max-w-xl h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-10" />
        </Reveal>

        {/* Quick links panel */}
        <Reveal delay={0.38} className="w-full max-w-xl">
          <div className="rounded-2xl border border-white/10 bg-white/4 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/40 to-transparent" />

            {/* Panel header */}
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-white/8">
              <div className="w-7 h-7 rounded-lg bg-[#ff5d22]/15 border border-[#ff5d22]/25 flex items-center justify-center">
                <Search size={12} className="text-[#ff5d22]" />
              </div>
              <span className="text-[9px] font-black uppercase tracking-[0.2em] text-white/40">
                Popular Destinations
              </span>
            </div>

            {/* Link grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3">
              {QUICK_LINKS.map((link, i) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`group flex items-center justify-between gap-2 px-4 py-3.5 text-[11px] font-bold text-white/65 hover:text-white hover:bg-white/6 transition-all duration-200 ${
                    i < QUICK_LINKS.length - (QUICK_LINKS.length % 3 || 3)
                      ? 'border-b border-white/6'
                      : ''
                  } ${i % 3 !== 2 ? 'border-r border-white/6' : ''}`}
                >
                  <span>{link.label}</span>
                  <ArrowRight size={10} className="opacity-0 group-hover:opacity-100 transition-opacity -translate-x-1 group-hover:translate-x-0 duration-200" />
                </Link>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Bottom tag */}
        <Reveal delay={0.44}>
          <div className="mt-10 flex items-center gap-2 text-white/25 text-[10px] font-bold">
            <Zap size={10} className="text-[#ff5d22]/50" />
            <span>AppGallop · Revenue Orchestration, Billing & Marketplace Platform</span>
          </div>
        </Reveal>

      </div>
    </div>
  );
}
